import { forwardRef, useContext, useEffect, useState, MutableRefObject } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { MappingContext } from '../contexts/MappingContext';
import type { Editor as TinyMCEEditorInstance } from 'tinymce';

import {
  AUTHORING_COLORS,
  DOCUMENT_MARGINS,
  DOCUMENT_STYLES,
  MSO_DEFINITIONS,
} from '../constants/colors';
import { REACT_APP_TINYMCE_API_KEY } from '../constants/config';
import { setupEditorClipboardSupport } from '../helpers/generalizationHelpers';
import Slider from './elements/Slider';
import { EditorRef, EditorType, EditorTypeEnum } from '../types';

export interface ITinyEditor {
  disabled: boolean;
  editorType: EditorType;
  ref: EditorRef;
  editorOptions?: (editor: TinyMCEEditorInstance) => void;
  initialize?: (evt: unknown, editor: TinyMCEEditorInstance) => void;
  onClickHandler?: (event: MouseEvent, editor: TinyMCEEditorInstance) => void;
  content?: string;
  scroll?: () => void;
  handleActivate?: (event: { relatedTarget: TinyMCEEditorInstance | null }) => void;
  height?: string;
  menuBar?: string | boolean;
  toolBar?: string | boolean;
  pluginsOverride?: string;
  onBlur?: () => void;
  onFocus?: () => void;
}

const TinyEditor = forwardRef<EditorRef, ITinyEditor>((props, ref) => {
  const [fontSize, setFontSize] = useState(100);

  const {
    editorType = EditorTypeEnum.Target,
    disabled,
    initialize,
    editorOptions,
    onClickHandler,
    handleActivate,
    content,
    scroll,
    height = '60vh',
    toolBar,
    menuBar,
    pluginsOverride,
    onBlur,
    onFocus,
  } = props;

  const { sourceFileDoc, editor1Content, editor1Ref, editor2Ref, setEditor1Content } =
    useContext(MappingContext);

  const basicToolbar =
    'bold italic underline strikethrough | backcolor tablecellbackgroundcolor | searchreplace | fontsize | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist | forecolor removeformat | table | charmap';
  const expandedToolbar =
    'undo |bold italic underline strikethrough backcolor tablecellbackgroundcolor | searchreplace | blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist | forecolor removeformat | table | pagebreak | charmap | fullscreen  preview save print | ltr rtl | tableofcontents typography formatpainter footnotes permanentpen';
  const basicPlugins = 'table searchreplace charmap advlist lists advtable';
  const expandedPlugins =
    'powerpaste preview importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media codesample table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount charmap emoticons tableofcontents typography autocorrect advtable formatpainter footnotes permanentpen';

  const plugins =
    pluginsOverride ?? (editorType === EditorTypeEnum.Target ? expandedPlugins : basicPlugins);
  const toolbar =
    toolBar ?? (editorType === EditorTypeEnum.Target ? expandedToolbar : basicToolbar);
  const menubar =
    (menuBar ?? editorType === EditorTypeEnum.Target)
      ? 'file edit view insert format tools table help'
      : false;

  const removed_menuitems =
    (menuBar ?? editorType === EditorTypeEnum.Target)
      ? 'newdocument, restoredraft, print, code, visualaid, media, codesample, emoticons, anchor, insertdatetime, codeformat, '
      : undefined;

  const editorRef = (ref ||
    (editorType === EditorTypeEnum.Target
      ? editor1Ref
      : editor2Ref)) as MutableRefObject<TinyMCEEditorInstance | null>;
  const editorContent = editorType === EditorTypeEnum.Target ? editor1Content : sourceFileDoc;

  type ZoomStyle = CSSStyleDeclaration & { zoom?: string };

  const handleFontSizeChange = (value: number) => {
    setFontSize(value);
    const editorBody = editorRef.current?.getBody() as HTMLElement | undefined;
    if (editorBody) {
      const zoomStyle = editorBody.style as ZoomStyle;
      const currentZoom = parseInt(zoomStyle.zoom || '100', 10);
      const currentWidth =
        parseInt(editorBody.style.width) || parseInt(window.getComputedStyle(editorBody).width);

      const width = (currentWidth * 100) / currentZoom;
      editorBody.style.width = `${width * (value / 100)}px`;
      zoomStyle.zoom = `${value}%`;
    }
  };

  useEffect(() => {
    if (editorType === EditorTypeEnum.Target || editorType === EditorTypeEnum.Source) {
      setEditor1Content({ html: '' });
    }

    return () => {
      if (editorType === EditorTypeEnum.Target || editorType === EditorTypeEnum.Source) {
        setEditor1Content({ html: '' });
      }
    };
  }, [editorType, setEditor1Content]);

  // Handle content based on editor type and content availability
  const getInitialValue = () => {
    switch (editorType) {
      case EditorTypeEnum.Session:
        return content;
      default:
        return content ?? editorContent.html;
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden min-h-0">
      <Editor
        apiKey={REACT_APP_TINYMCE_API_KEY}
        disabled={disabled}
        onInit={(evt, editor) => (editorRef.current = editor)}
        initialValue={getInitialValue()}
        init={{
          powerpaste_allow_local_images: true,
          paste_data_images: true,
          paste_as_text: false,
          powerpaste_word_import: 'merge',
          height: height,
          skin: 'fluent',
          icons: 'material',
          elementpath: false,
          content_css: 'fluent',
          branding: false,
          contextmenu: 'acceptValue selectValue',
          contextmenu_avoid_overlap: '.mce-spelling-word',
          menubar: menubar,
          menu: {
            format: {
              title: 'Format',
              items:
                'bold italic underline strikethrough superscript subscript codeformat | styles | removeformat',
            },
          },
          removed_menuitems: removed_menuitems,
          plugins: plugins,
          table_toolbar: '',
          toolbar: toolbar,
          visual: false,
          content_style:
            DOCUMENT_STYLES +
            DOCUMENT_MARGINS +
            MSO_DEFINITIONS +
            `
            ::selection {
              background: ${AUTHORING_COLORS.NARRATIVE_SELECTION} !important;
            }
            ::-moz-selection {
              background: ${AUTHORING_COLORS.NARRATIVE_SELECTION} !important;
            }
          `,
          deprecation_warnings: false,
          setup: (editor: TinyMCEEditorInstance) => {
            editorOptions?.(editor);
            editor.on('init', () => {
              try {
                editor.formatter.register('bold', {
                  inline: 'b',
                  toggle: true,
                  remove: 'all',
                });
                editor.formatter.register('italic', {
                  inline: 'i',
                  toggle: true,
                  remove: 'all',
                });
                editor.formatter.register('underline', {
                  inline: 'u',
                  toggle: true,
                  remove: 'all',
                });
                editor.formatter.register('strikethrough', {
                  inline: 'span',
                  toggle: true,
                  styles: { 'text-decoration': 'line-through' },
                  remove: 'all',
                });
              } catch (e) {
                // Formats already registered by TinyMCE
              }
              setupEditorClipboardSupport(editor, {
                readOnlyClipboard: editorType === EditorTypeEnum.Source,
              });
            });
          },
          spellchecker_language: 'en',
          typography_default_lang: 'en-US',
          table_use_colgroups: false,
          style_formats_merge: false,
          importcss_append: true,
        }}
        onSetContent={(evt, editor) => {
          initialize?.(evt, editor);
          scroll?.();
        }}
        onActivate={handleActivate}
        onClick={onClickHandler}
        // onBeforeAddUndo={() => {
        //   return false;
        // }}
        // onUndo={() => {}}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      <Slider value={fontSize} changeHandler={handleFontSizeChange} min={80} max={130} step={2} />
    </div>
  );
});

TinyEditor.displayName = 'TinyEditor';
export default TinyEditor;
